import type { FC } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { SoldOut } from '@wwe-portal/ui/features/store-revamp';
import lockIconWebp from '@wwe-portal/ui/assets/images/flagship/lock-icon.webp';

import { OfferTimeCountDown } from '../component/offer-time-countdown';
import { NotifPip } from '../flagship/notif-pip';
import { LayoutTypeEnum, type OfferCardProps } from '../type';
import { OfferCardTypeEnum } from '@wwe-portal/entities/offer.type';

import { DripOfferSingle1Card } from './drip-offer-single1';
import { OfferHeader } from './offer-header';
import { OfferItems } from './offer-items';
import { OfferItemsRevamp } from './offer-items-revamp';
import { OfferLayout } from './offer-layout';
import { ResponseOfferCardBackground } from './responsive-offer-card-background';
import { StockQuantity } from './stock-quantity';

export const OfferSingle1Card: FC<OfferCardProps> = ({
  id,
  backgroundImage,
  dynamicBackgroundImage,
  title,
  price,
  strikethroughPrice,
  endDate,
  items,
  quantity,
  maxQuantity,
  style,
  onClick,
  onPurchaseClick,
  overlay,
  hasPip,
  badgeIcon,
  badgeText,
  hasContest,
  type,
  showStock,
  isBlocked = false,
  soldOut,
  totalItems,
  layoutType = LayoutTypeEnum.BGAndItemsCentered,
  offerDescriptionText,
  isDripOffer,
  dripDays,
  dripLabels,
}) => {
  if (isDripOffer) {
    return (
      <DripOfferSingle1Card
        backgroundImage={backgroundImage}
        dripDays={dripDays}
        dripLabels={dripLabels}
        dynamicBackgroundImage={dynamicBackgroundImage}
        endDate={endDate}
        hasPip={hasPip}
        id={id}
        isBlocked={isBlocked}
        items={items}
        onClick={onClick}
        onPurchaseClick={onPurchaseClick}
        overlay={overlay}
        price={price}
        soldOut={soldOut}
        strikethroughPrice={strikethroughPrice}
        style={style}
        title={title}
        totalItems={totalItems}
        type={type}
      />
    );
  }

  return (
    <div className="relative" data-id={id}>
      <ClickableDiv
        className="aspect-single-1 font-poppins"
        onClick={onClick}
        style={style}
      >
        <div className="absolute inset-0 overflow-clip rounded-b-[10px]">
          <div className="absolute -bottom-[calc(700%/399)] -left-[calc(700%/712)] -right-[calc(700%/712)] -top-[calc(700%/399)]">
            <ResponseOfferCardBackground
              backgroundImage={backgroundImage}
              dynamicBackgroundImage={dynamicBackgroundImage}
              type={type}
            />
          </div>
        </div>
        <OfferLayout
          cardType={OfferCardTypeEnum.SINGLE1}
          footer={
            price != null && (
              <ClickableDiv
                className="absolute inset-0"
                data-slot="price"
                onClick={onPurchaseClick}
              >
                <div className="drop-shadow-text-2 flex h-full flex-row items-center justify-center space-x-[5px] font-semibold text-white">
                  {strikethroughPrice ? (
                    <span className="pr-[2px] text-left text-xs line-through drop-shadow-lg">
                      {strikethroughPrice}
                    </span>
                  ) : null}
                  <span className="flex h-[18px] items-center justify-center gap-x-1 text-center text-base font-semibold capitalize">
                    {price}
                  </span>
                </div>
                {Boolean(isBlocked) && (
                  <img
                    alt="locked"
                    className="absolute inset-y-[17%] right-1 h-[21px] md:h-[22px]"
                    src={lockIconWebp}
                  />
                )}
              </ClickableDiv>
            )
          }
          header={<OfferHeader maxFont={16}>{title}</OfferHeader>}
          items={
            layoutType === LayoutTypeEnum.BGTextAndItemsCentered ? (
              <OfferItemsRevamp
                description={offerDescriptionText}
                items={items}
                maxItem={2}
                totalItems={totalItems}
              />
            ) : (
              <OfferItems
                itemWidth="calc(4000%/348)"
                items={items}
                maxItem={4}
                totalItems={totalItems}
              />
            )
          }
          stockQuantity={
            <StockQuantity
              badgeIcon={badgeIcon}
              badgeText={badgeText}
              hasContest={hasContest}
              maxQuantity={maxQuantity}
              quantity={quantity}
              showStock={showStock && !soldOut}
              type={type}
            />
          }
          timeLeft={
            endDate ? (
              <OfferTimeCountDown className="h-full" endDate={endDate} />
            ) : null
          }
        />
        {overlay}
      </ClickableDiv>
      {hasPip && !soldOut ? <NotifPip /> : null}
      {soldOut && <SoldOut type={OfferCardTypeEnum.SINGLE1} />}
    </div>
  );
};
